import { z } from 'zod';
import {
  PACKAGE_TIER_KEY_VALUES,
  PACKAGE_TIER_KEYS,
  PLATFORM_VERTICAL_KEY_VALUES,
  ThemeIdSchema,
  type PackageTierKey,
  type PlatformVerticalKey,
  type ThemeId,
} from '../catalog/platformCatalogIds';
import { PLATFORM_VERTICAL_MATRIX, resolveVerticalDefaultTheme } from '../matrix/platformVerticalMatrix';

export const GASTRONOMIE_PASS_TEMPLATE = {
  fineDining: 'fine_dining',
  barBistro: 'bar_bistro',
  fastCasual: 'fast_casual',
  foodTruck: 'food_truck',
} as const;

export const GASTRONOMIE_PASS_TEMPLATE_KEY_VALUES = [
  GASTRONOMIE_PASS_TEMPLATE.fineDining,
  GASTRONOMIE_PASS_TEMPLATE.barBistro,
  GASTRONOMIE_PASS_TEMPLATE.fastCasual,
  GASTRONOMIE_PASS_TEMPLATE.foodTruck,
] as const;

export type GastronomiePassTemplate = (typeof GASTRONOMIE_PASS_TEMPLATE_KEY_VALUES)[number];

const passTokenIdSchema = z
  .string()
  .regex(
    /^pass_[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
    { message: 'pass_token_id must be a UUID prefixed with pass_' },
  );

/**
 * Marketplace → studio onboarding handoff payload.
 * `v: 2` kept for sessionStorage / cookies; site selection fields are optional
 * so older links (`pass` + `tpl` only) still parse.
 */
export const MarketingPassBootstrapSchema = z.object({
  v: z.literal(2),
  pass_token_id: passTokenIdSchema,
  target_industry_template: z.enum(GASTRONOMIE_PASS_TEMPLATE_KEY_VALUES),
  onboarding_jargon_level: z.literal('business_owner'),
  ui_interaction_mode: z.literal('strict_content_only'),
  /** Explicit site config from marketplace template card (preferred over vertical default). */
  vertical: z.enum(PLATFORM_VERTICAL_KEY_VALUES).optional(),
  package_tier: z.enum(PACKAGE_TIER_KEY_VALUES).optional(),
  theme_id: ThemeIdSchema.optional(),
});

export type MarketingPassBootstrap = z.infer<typeof MarketingPassBootstrapSchema>;

/** Site config stamped from a marketplace template variant. */
export interface MarketingPassSiteSelection {
  vertical: PlatformVerticalKey;
  packageTier: PackageTierKey;
  themeId: ThemeId;
}

export const MARKETING_PASS_BOOTSTRAP_STORAGE_KEY = 'web-studio-marketing-bootstrap';

export function createPassTokenId(): string {
  const uuid =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : fallbackUuid();
  return `pass_${uuid}`;
}

function fallbackUuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function isThemeOfferedForVertical(vertical: PlatformVerticalKey, themeId: ThemeId): boolean {
  return (
    PLATFORM_VERTICAL_MATRIX[vertical]?.themes.some((theme) => theme.id === themeId) ?? false
  );
}

export function createMarketingPassBootstrap(
  template: GastronomiePassTemplate,
  selection?: MarketingPassSiteSelection,
): MarketingPassBootstrap {
  const base: MarketingPassBootstrap = {
    v: 2,
    pass_token_id: createPassTokenId(),
    target_industry_template: template,
    onboarding_jargon_level: 'business_owner',
    ui_interaction_mode: 'strict_content_only',
  };

  if (
    !selection ||
    !isThemeOfferedForVertical(selection.vertical, selection.themeId) ||
    !(PACKAGE_TIER_KEY_VALUES as readonly string[]).includes(selection.packageTier)
  ) {
    return base;
  }

  return {
    ...base,
    vertical: selection.vertical,
    package_tier: selection.packageTier,
    theme_id: selection.themeId,
  };
}

/** Resolve vertical / tier / theme for onboarding, with tpl fallbacks. */
export function resolveMarketingPassSiteSelection(
  bootstrap: MarketingPassBootstrap,
  mapTemplate: (template: GastronomiePassTemplate) => {
    vertical: PlatformVerticalKey;
    packageTier: PackageTierKey;
  },
): MarketingPassSiteSelection {
  const mapped = mapTemplate(bootstrap.target_industry_template);
  const vertical = bootstrap.vertical ?? mapped.vertical;
  const packageTier = bootstrap.package_tier ?? mapped.packageTier ?? PACKAGE_TIER_KEYS.recommended;
  const fromBootstrap =
    bootstrap.theme_id && isThemeOfferedForVertical(vertical, bootstrap.theme_id)
      ? bootstrap.theme_id
      : undefined;
  const themeId =
    fromBootstrap ??
    resolveVerticalDefaultTheme(vertical) ??
    resolveVerticalDefaultTheme(mapped.vertical)!;

  return { vertical, packageTier, themeId };
}
